import { useBridgeScannerStore } from "@/features/bridge-scanner/stores/bridge-scanner.store";
import { useEffect, useRef, useState } from "react";

interface UsePollingTimeoutOptions {
  timeoutMinutes?: number;
}

export const usePollingTimeout = (
  isPollingEnabled: boolean,
  options?: UsePollingTimeoutOptions,
) => {
  const setLiveMode = useBridgeScannerStore((store) => store.setLiveMode);

  const timeoutMinutes = options?.timeoutMinutes ?? 10; // Default is 10 minutes
  const [hasTimedOut, setHasTimedOut] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isPollingEnabled) {
      // Clear previous timer and reset timeout state
      if (timerRef.current) clearTimeout(timerRef.current);
      setHasTimedOut(false);

      // Start a new timer
      timerRef.current = setTimeout(() => {
        setHasTimedOut(true);
        setLiveMode(false);
      }, timeoutMinutes * 60 * 1000); // Convert minutes to ms
    } else {
      // If polling is disabled, clear timer
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null; // Clear reference for safety
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isPollingEnabled, setLiveMode, timeoutMinutes]);

  return {
    hasTimedOut,
    resetTimeout: () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setHasTimedOut(false);

      if (isPollingEnabled) {
        timerRef.current = setTimeout(() => {
          setHasTimedOut(true);
        }, timeoutMinutes * 60 * 1000);
      }
    },
  };
};
